import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  getWeddingProfileDisplayRows,
  parseWeddingListingDescription,
} from '../../utils/parseWeddingListingDescription';
import {
  formatListingItemFlawLabel,
  normalizeListingItemFlaws,
} from '../../constants/listingItemFlaws';
import { WEDDING_PRE_LOVED_CATEGORY_DATA } from '../../constants/weddingCategories';
import { isFreeListing } from '../../utils/formatPrice';
import { isContactOnlyListingCategory } from '../../constants/contactOnlyCategories';
import { looksLikePaintingDimensions } from '../../utils/paintingDimensions';

const WEDDING_ESSENTIAL_CATEGORY_SET = new Set(Object.keys(WEDDING_PRE_LOVED_CATEGORY_DATA));

export const AUTHENTICITY_CHECK_FEE_EGP = 500;

/** Authenticity verification is offered on luxury marketplace listings only. */
export function isLuxuryListing(item) {
  if (!item) return false;
  if (item.listing_type === 'luxury') return true;
  const flag = item.is_luxury;
  return flag === true || flag === 1 || flag === 'true';
}

export function isWeddingEssentialsListing(item) {
  if (!item) return false;
  if (item.listing_type === 'wedding_essentials') return true;
  return Boolean(item.category && WEDDING_ESSENTIAL_CATEGORY_SET.has(item.category));
}

function isTruthyReceiptFlag(value) {
  return value === true || value === 1 || value === 'true';
}

export function hasReceiptAvailable(item) {
  if (!item || !isTruthyReceiptFlag(item.has_receipt_or_guarantee)) return false;
  return true;
}

const WEIGHT_BAND_LABELS = {
  under_2kg: 'Less than 2 kg',
  under_5kg: 'Less than 5 kg',
  under_10kg: 'Less than 10 kg',
  under_20kg: 'Less than 20 kg',
};

export function formatWeightEstimateBand(value) {
  if (!value) return null;
  const key = String(value).trim();
  if (!key) return null;
  return WEIGHT_BAND_LABELS[key] || key.replace(/_/g, ' ');
}

export const AUTHENTICITY_CHECK_CONFIRM_BODY =
  'Flea will verify this luxury item\'s authenticity before you buy. The service fee is 500 EGP.';

export const AUTHENTICITY_CHECK_CONFIRM_NOTE =
  'Do you want to complete this request? Alternatively, you may inspect the item yourself (e.g. at pickup) at your own responsibility.';

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  margin-top: 1rem;
`;

const Chip = styled.div`
  display: flex;
  align-items: center;
  background-color: #34c759;
  color: #fff;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
`;

const AnonymousBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0 0 0.75rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  color: #4b5563;
  font-size: 0.8125rem;
  font-weight: 600;
  font-style: normal;
`;

const MetaBlock = styled.div`
  margin-bottom: 1.5rem;
`;

const MetaLine = styled.p`
  margin: 0.35rem 0;
  color: var(--text-primary);
`;

const Description = styled.p`
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 1.5rem;
  white-space: pre-wrap;
`;

export function isListingAnonymous(value) {
  if (value === true || value === 1) return true;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  return false;
}

export function PlatformBenefitChips({ item } = {}) {
  const { t } = useTranslation();
  const benefitLabel = isWeddingEssentialsListing(item)
    ? t('itemDetail.reserveFitting')
    : t('itemDetail.tryBeforeBuy');
  return (
    <ChipsRow>
      <Chip><span>✨</span><span>{benefitLabel}</span></Chip>
    </ChipsRow>
  );
}

const FreeListingPanel = styled.div`
  margin: 0 0 1rem;
  padding: 0.9rem 1rem;
  border-radius: 12px;
  background: #ecfdf3;
  border: 1px solid #86efac;
  color: #14532d;
`;

const FreeListingLine = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.45;

  & + & {
    margin-top: 0.45rem;
  }
`;

export function FreeListingNotice({ item }) {
  const { t } = useTranslation();
  if (!isFreeListing(item)) return null;
  return (
    <FreeListingPanel aria-label={t('itemDetail.freeItemMonthlyLimit')}>
      <FreeListingLine><strong>🎁 {t('itemDetail.freeItemMonthlyLimit')}</strong></FreeListingLine>
      <FreeListingLine>{t('itemDetail.freeItemLeaveForOthers')}</FreeListingLine>
    </FreeListingPanel>
  );
}

export function isContactOnlyListing(item) {
  return isContactOnlyListingCategory(item?.category);
}

export function isComputersElectronicsListing(itemOrCategory) {
  const category =
    typeof itemOrCategory === 'string'
      ? itemOrCategory
      : itemOrCategory?.category || '';
  return category === 'Computers & Electronics';
}

export function ElectronicsListingNotice({ item }) {
  const { t } = useTranslation();
  if (!isComputersElectronicsListing(item)) return null;
  return (
    <ElectronicsPanel>
      <ElectronicsTitle>{t('itemDetail.electronicsNoticeTitle')}</ElectronicsTitle>
      <ElectronicsBody>{t('itemDetail.electronicsNoticeBody')}</ElectronicsBody>
    </ElectronicsPanel>
  );
}

const ElectronicsPanel = styled.div`
  margin: 0 0 1rem;
  padding: 0.9rem 1rem;
  border-radius: 12px;
  background: #eff6ff;
  border: 1px solid #93c5fd;
  color: #1e3a8a;
`;

const ElectronicsTitle = styled.p`
  margin: 0 0 0.45rem;
  font-weight: 700;
  font-size: 0.95rem;
`;

const ElectronicsBody = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.45;
  color: #1e40af;
`;

export function getContactListingPhoneState(item) {
  if (!isContactOnlyListing(item)) return 'hidden';
  const phone = (item?.seller_phone || '').trim();
  if (isComputersElectronicsListing(item)) {
    return phone ? 'phone' : 'missing';
  }
  return 'hidden';
}

const ReceiptBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0 0 0.75rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background: #ecfdf3;
  border: 1px solid #86efac;
  color: #166534;
  font-size: 0.8125rem;
  font-weight: 600;
`;

export function ReceiptAvailableBadge({ item }) {
  const { t } = useTranslation();
  if (!hasReceiptAvailable(item)) return null;
  return (
    <ReceiptBadge aria-label={t('itemDetail.receiptAvailable')}>
      🟢 {t('itemDetail.receiptAvailable')}
    </ReceiptBadge>
  );
}

export function AnonymousSellerNotice({ isAnonymous }) {
  const { t } = useTranslation();
  if (!isListingAnonymous(isAnonymous)) return null;
  return (
    <AnonymousBadge aria-label={t('itemDetail.postedAnonymously')}>
      🙈 {t('itemDetail.postedAnonymously')}
    </AnonymousBadge>
  );
}

const ProfileBlock = styled.div`
  margin-bottom: 1.5rem;
`;

const ProfileTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.75rem;
`;

const FlawsBlock = styled.div`
  margin: 0 0 1rem;
`;

const FlawsTitle = styled.p`
  margin: 0 0 0.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary, #111827);
`;

const FlawsList = styled.ul`
  margin: 0;
  padding-left: 1.15rem;
  color: var(--text-primary, #374151);
`;

const FlawsItem = styled.li`
  margin-bottom: 0.25rem;
`;

function displayText(value) {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed || null;
}

/** Brand shown on detail pages: catalog name, else typed-in name, hidden for "no brand". */
export function resolveListingBrand(item) {
  if (!item) return null;
  if (item.is_no_brand === true || item.is_no_brand === 1 || item.is_no_brand === 'true') return null;
  const name = (item.brand_name || item.custom_brand_name || '').trim();
  return name || null;
}

export function ListingMetaFields({
  category,
  subcategory,
  bookSubcategory,
  brand,
  condition,
  showCondition = true,
  color,
  size,
  availableSizes,
  material,
  length,
  bookType,
  bookLanguage,
  bookAudience,
  petType,
  isHijabi,
  fragile,
  weightOver15kg,
  itemCode,
  description,
  itemFlaws,
}) {
  const { t } = useTranslation();
  const { cleanDescription, profile, profileType } = useMemo(
    () => parseWeddingListingDescription(description),
    [description],
  );
  const profileRows = useMemo(
    () => getWeddingProfileDisplayRows(profile, profileType || 'dress'),
    [profile, profileType],
  );
  const normalizedFlaws = useMemo(() => normalizeListingItemFlaws(itemFlaws), [itemFlaws]);
  const subcategoryValue = displayText(subcategory) || displayText(bookSubcategory);
  const brandValue = displayText(brand);
  const colorValue = displayText(color);
  const sizeValue = displayText(availableSizes) || displayText(size);
  const materialValue = displayText(material);
  const lengthValue = displayText(length);
  const bookTypeValue = displayText(bookType);
  const bookLanguageValue = displayText(bookLanguage);
  const bookAudienceValue = displayText(bookAudience);
  const petTypeValue = displayText(petType);
  const isFragile = isTruthyReceiptFlag(fragile);
  const isHeavy = isTruthyReceiptFlag(weightOver15kg);
  const profileHasColor = profileRows.some((row) => row.label === 'Color');
  const profileHasSize = profileRows.some((row) => row.label === 'Size');

  return (
    <>
      <MetaBlock>
        {category ? <MetaLine><strong>{t('itemDetail.category')}:</strong> {category}</MetaLine> : null}
        {subcategoryValue ? (
          <MetaLine><strong>{t('itemDetail.subcategory')}:</strong> {subcategoryValue}</MetaLine>
        ) : null}
        {brandValue ? (
          <MetaLine><strong>{t('itemDetail.brand')}:</strong> {brandValue}</MetaLine>
        ) : null}
        {showCondition && condition ? <MetaLine><strong>{t('itemDetail.condition')}:</strong> {condition}</MetaLine> : null}
        {colorValue && !profileHasColor ? (
          <MetaLine><strong>{t('itemDetail.color')}:</strong> {colorValue}</MetaLine>
        ) : null}
        {sizeValue && !profileHasSize ? (
          <MetaLine>
            <strong>
              {looksLikePaintingDimensions(sizeValue)
                ? t('itemDetail.dimensions', 'Dimensions')
                : t('itemDetail.size')}
              :
            </strong>{' '}
            {sizeValue}
          </MetaLine>
        ) : null}
        {materialValue ? (
          <MetaLine><strong>{t('itemDetail.material')}:</strong> {materialValue}</MetaLine>
        ) : null}
        {lengthValue ? (
          <MetaLine><strong>{t('itemDetail.length')}:</strong> {lengthValue}</MetaLine>
        ) : null}
        {bookTypeValue ? (
          <MetaLine><strong>{t('itemDetail.bookType')}:</strong> {bookTypeValue}</MetaLine>
        ) : null}
        {bookLanguageValue ? (
          <MetaLine><strong>{t('itemDetail.bookLanguage')}:</strong> {bookLanguageValue}</MetaLine>
        ) : null}
        {bookAudienceValue ? (
          <MetaLine><strong>{t('itemDetail.bookAudience')}:</strong> {bookAudienceValue}</MetaLine>
        ) : null}
        {petTypeValue ? (
          <MetaLine><strong>{t('itemDetail.petType')}:</strong> {petTypeValue}</MetaLine>
        ) : null}
        {isHijabi !== null && isHijabi !== undefined ? (
          <MetaLine><strong>Designed for:</strong> {isHijabi ? 'Hijabi' : 'Non-Hijabi'}</MetaLine>
        ) : null}
        {isFragile ? (
          <MetaLine><strong>{t('itemDetail.fragile')}:</strong> {t('itemDetail.yes')}</MetaLine>
        ) : null}
        {isHeavy ? (
          <MetaLine><strong>{t('itemDetail.weightOver15kg')}:</strong> {t('itemDetail.yes')}</MetaLine>
        ) : null}
        {itemCode ? (
          <MetaLine>
            <strong>{t('itemDetail.itemCode')}:</strong>{' '}
            <span style={{ fontFamily: 'monospace', fontSize: '0.9rem', color: 'var(--text-light)' }}>
              {itemCode}
            </span>
          </MetaLine>
        ) : null}
      </MetaBlock>

      {normalizedFlaws.length > 0 ? (
        <FlawsBlock>
          <FlawsTitle>{t('itemDetail.flawsTitle')}</FlawsTitle>
          <FlawsList>
            {normalizedFlaws.map((key) => (
              <FlawsItem key={key}>{formatListingItemFlawLabel(key)}</FlawsItem>
            ))}
          </FlawsList>
        </FlawsBlock>
      ) : null}

      {cleanDescription ? (
        <>
          <MetaLine>
            <strong>{t('itemDetail.description')}:</strong>
          </MetaLine>
          <Description>{cleanDescription}</Description>
        </>
      ) : null}

      {profileRows.length > 0 ? (
        <ProfileBlock>
          <ProfileTitle>{t('itemDetail.listingDetails')}</ProfileTitle>
          {profileRows.map((row) => (
            <MetaLine key={row.label}>
              <strong>{row.label}:</strong> {row.value}
            </MetaLine>
          ))}
        </ProfileBlock>
      ) : null}
    </>
  );
}
